import { useEffect, useState } from 'react';
import { nomenclatureApi } from '@/services/nomenclatureApi';
import type { NomenclatureItem } from '@/features/common/types/NomenclatureItem';

/** The nomenclature code seeded by V017. Renaming it here without renaming the seed empties the legend. */
export const MAP_SYMBOLOGY = 'MAP_SYMBOLOGY';

export interface MapCategory {
  /**
   * The nomenclature item's id. `geo_point.category_id` points at this, so anything matching a
   * point to its category needs the id — the code only matches a derived point's subjectType.
   * Empty on the built-in fallback, which is why a fallback category cannot colour a stored point.
   */
  id: string;
  code: string;
  nameRo: string;
  nameEn: string | null;
  color: string;
}

/**
 * The colours the design fixed, kept here only as a fallback.
 *
 * <p>The administrable copy lives in the MAP_SYMBOLOGY nomenclature (V017 for the items, V038 for
 * the colours), which is what FR-042 requires: "categorii de simbologie a hărții" administrable
 * without a code change. This list exists so the map still draws when that request fails — an
 * anonymous page losing its legend because a fetch timed out is worse than a stale legend.
 *
 * <p>{@code mapSymbology.test.ts} fails if it drifts from what the migration seeds. A fallback
 * that quietly disagrees with the source of truth is worse than no fallback: it turns an outage
 * into wrong colours nobody can explain.
 */
export const FALLBACK_CATEGORIES: MapCategory[] = [
  {
    id: '',
    code: 'ADVISOR',
    nameRo: 'Consilieri agricoli',
    nameEn: 'Agricultural advisers',
    color: '#0058d2',
  },
  {
    id: '',
    code: 'TRAINING',
    nameRo: 'Instituții de formare',
    nameEn: 'Training institutions',
    color: '#006369',
  },
  {
    id: '',
    code: 'RESEARCH',
    nameRo: 'Organizații de cercetare',
    nameEn: 'Research organisations',
    color: '#00357e',
  },
  { id: '', code: 'EVENT', nameRo: 'Evenimente', nameEn: 'Events', color: '#66a1a5' },
  {
    id: '',
    code: 'SERVICE_PROVIDER',
    nameRo: 'Servicii de consiliere',
    nameEn: 'Advisory services',
    color: '#99bced',
  },
  {
    id: '',
    code: 'INTERVENTION',
    nameRo: 'Intervenții teritoriale',
    nameEn: 'Territorial interventions',
    color: '#e08a2c',
  },
];

/** Marker colour for a category with no colour recorded — grey, and visibly not a category colour. */
export const UNCATEGORISED_COLOUR = '#757575';

/** Reads `{"color":"#…"}`, falling back rather than throwing on anything unexpected. */
export function colourOf(item: NomenclatureItem, fallback: string): string {
  try {
    const parsed = JSON.parse(item.metadata || '{}') as { color?: unknown };
    return typeof parsed.color === 'string' && parsed.color.trim() ? parsed.color : fallback;
  } catch {
    return fallback;
  }
}

export function toCategories(items: NomenclatureItem[]): MapCategory[] {
  return items.map((item) => ({
    id: item.id,
    code: item.code,
    nameRo: item.nameRo,
    nameEn: item.nameEn,
    color: colourOf(
      item,
      FALLBACK_CATEGORIES.find((c) => c.code === item.code)?.color ?? UNCATEGORISED_COLOUR,
    ),
  }));
}

/**
 * The map's categories, colours and labels — from the nomenclature, not from the bundle.
 *
 * <p>They used to exist in three places that had already drifted: a hardcoded list in MapPage, a
 * second list on the home page, and the names in an i18n dictionary that no longer matched the
 * seeded ones. One source now, administrable from "Configurare sistem".
 */
export function useMapSymbology(): MapCategory[] {
  const [categories, setCategories] = useState<MapCategory[]>(FALLBACK_CATEGORIES);

  useEffect(() => {
    nomenclatureApi
      .publicItems<NomenclatureItem>(MAP_SYMBOLOGY)
      // An empty vocabulary means someone retired every category; that is not a reason to draw an
      // empty legend over a map full of markers, so the fallback stands.
      .then((items) => setCategories(items.length ? toCategories(items) : FALLBACK_CATEGORIES))
      .catch(() => setCategories(FALLBACK_CATEGORIES));
  }, []);

  return categories;
}

/** The label to show; Romanian is required everywhere, English only where it exists. */
export function categoryLabel(category: MapCategory, language: string): string {
  return language === 'en' && category.nameEn ? category.nameEn : category.nameRo;
}

/**
 * The symbology code a map feature belongs to.
 *
 * The legend, the colours and the layer filters are all keyed by CODE. `subjectType` is that code
 * only for points a producer creates; a point an administrator places on /app/admin/harta carries
 * a chosen category and a `subjectType` that is not a legend code at all. Keying off `subjectType`
 * therefore drew every administered point grey, under no legend entry, and no filter could switch
 * it on or off — while the admin screen showed the same point correctly coloured, which is the
 * version of the bug that looks like it worked.
 *
 * The server resolves `categoryCode` from the chosen category and falls back to `subjectType`
 * itself, so this only has to handle a feed that predates that field.
 */
export function symbologyCode(properties: Record<string, unknown> | null | undefined): string {
  const p = properties ?? {};
  const code = p.categoryCode ?? p.subjectType;
  return code == null ? '' : String(code);
}

/**
 * How many points each symbology category has in a feed.
 *
 * The legend is also the category filter, so an entry with no points is a control that visibly
 * does nothing: a visitor clicking it sees the map not change, and cannot tell whether Moldova has
 * no research organisations or whether the layer is broken. Two of the six categories are in
 * exactly that state in production — nothing creates points for TRAINING or RESEARCH — and the
 * tender does not ask for either on the map, so the honest answer is to show the count rather than
 * to withdraw the category or invent a producer for it.
 *
 * Counted over the whole feed, which the map loads unfiltered, so the numbers do not shift as a
 * visitor pans.
 */
export function countByCategory(
  features: { properties?: Record<string, unknown> | null }[],
): Record<string, number> {
  return features.reduce<Record<string, number>>((tally, feature) => {
    const code = symbologyCode(feature.properties);
    tally[code] = (tally[code] ?? 0) + 1;
    return tally;
  }, {});
}

/**
 * Whether every category that actually has points is switched off.
 *
 * This decides between two very different messages on an empty map: "nothing matched your search"
 * and "you have switched every category off". It used to ask the question of every category in the
 * nomenclature, including ones with no points — and a category with none is never switched off,
 * because there is nothing to switch. So a single empty category made the answer permanently
 * false, and the "no active category" message became unreachable.
 *
 * That is the state of production: TRAINING and RESEARCH have no producer, so turning off all four
 * categories that do have points left a visitor reading "nothing found" for a map they had just
 * emptied themselves. It passed unnoticed because the developer seed fills all six.
 */
export function allCategoriesWithPointsAreOff(
  typesPresent: Iterable<string>,
  active: Record<string, boolean>,
): boolean {
  const types = [...new Set(typesPresent)];
  return types.length > 0 && types.every((code) => !(active[code] ?? true));
}
